import { lazy, Suspense } from 'react';

import { useBreakpoint } from '@hivebrite/design-system/Theme';

import type { UserBannerDataResponse } from 'hb-builder/api/userBanner/types';
import { Children as ChildrenBuilderRenderer } from 'hb-builder/components/common/Item';
import SuspenseFallback from 'hb-builder/components/common/SuspenseFallback';
import { ChildrenRenderer } from 'hb-builder/components/Renderer/ChildrenRenderer';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';
import { APP_TYPE } from 'hb-react/types/appType';

import UserBanner, { SIZES } from './components/UserBanner';

const UserBannerLoadingUI = {
  small: lazy(
    () =>
      import(
        'hb-builder/components/common/LoadingComponents/UserBannerLoader/UserBannerSmallLoaderUI'
      ),
  ),
  medium: lazy(
    () =>
      import(
        'hb-builder/components/common/LoadingComponents/UserBannerLoader/UserBannerMediumLoaderUI'
      ),
  ),
  large: lazy(
    () =>
      import(
        'hb-builder/components/common/LoadingComponents/UserBannerLoader/UserBannerLargeLoaderUI'
      ),
  ),
};

interface UserBannerLayoutProps {
  app?: APP_TYPE;
  isLoading?: boolean;
  item?: DocumentItem<SectionItemAttributes>;
  user?: UserBannerDataResponse;
}
const UserBannerLayout = ({
  app = APP_TYPE.PUBLIC_OFFICE,
  isLoading,
  item,
  user,
}: UserBannerLayoutProps) => {
  const breakpoint = useBreakpoint();
  const isBackOffice = app === APP_TYPE.BACKOFFICE;
  const size: string = SIZES[breakpoint];
  const props = {
    ChildrenComponent: isBackOffice
      ? ChildrenBuilderRenderer
      : ChildrenRenderer,
    item,
    isBackOffice,
    size,
    user,
  };

  if (isBackOffice) {
    return <UserBanner {...props} />;
  }

  const UserBannerLoading = UserBannerLoadingUI[size];

  return (
    <Suspense fallback={<SuspenseFallback />}>
      {isLoading || !user ? <UserBannerLoading /> : <UserBanner {...props} />}
    </Suspense>
  );
};

export default UserBannerLayout;
